// src/utils/allergyMessageUtil.js
// Enhanced contextual allergy message system

/**
 * Drug class pattern matching for context-aware messaging
 */
export const DRUG_PATTERNS = {
  PENICILLINS: ['illin'], // amoxicillin, ampicillin, penicillin, flucloxacillin, piperacillin
  CEPHALOSPORINS: ['cef', 'ceph'], // cefazolin, ceftriaxone, cefuroxime, cephalexin, etc.
  CARBAPENEMS: ['penem'], // meropenem, ertapenem, imipenem
};

/**
 * Contextual allergy messages - shorter and more specific
 */
export const ALLERGY_MESSAGES = {
  '#spot': {
    // Info message for children - penicillins are often still safe
    default: "Penicillin allergy labels are common in children but many children tolerate penicillin when given it again, either because the rash was not an allergy (e.g, it was a viral exanthem) or they have outgrown their allergy. Penicillin antibiotics are first line treatment for this condition and they can be prescribed again for patients with a history of mild rash.",
    severity: 'info',
    backgroundColor: '#e3f2fd',
    textColor: '#0d47a1'
  },
  
  '#immed': {
    // Immediate allergy - context depends on what's being recommended
    cephalosporin: "History of immediate (likely IgE-mediated) allergy to a penicillin antibiotic noted. Recent evidence demonstrates a very low risk of cross-reactivity with structurally dissimilar cephalosporin antibiotics as recommended here.",
    carbapenem: "History of immediate (likely IgE-mediated) allergy to a penicillin antibiotic noted. Recent evidence demonstrates a very low risk of cross-reactivity with carbapenem antibiotics as recommended here.",
    general: "Consider structurally dissimilar beta-lactams (cephalosporins/carbapenems) - low cross-reactivity risk.",
    severity: 'caution',
    backgroundColor: '#fff9e3',
    textColor: '#000'
  },
  
  '#anaphylaxis': {
    // Same as immediate but potentially more serious tone
    cephalosporin: "History of immediate (likely IgE-mediated) allergy to a penicillin antibiotic noted. Recent evidence demonstrates a very low risk of cross-reactivity with structurally dissimilar cephalosporin antibiotics as recommended here.",
    carbapenem: "History of immediate (likely IgE-mediated) allergy to a penicillin antibiotic noted. Recent evidence demonstrates a very low risk of cross-reactivity with carbapenem antibiotics as recommended here.",
    general: "Consider structurally dissimilar beta-lactams (cephalosporins/carbapenems) - low cross-reactivity risk.",
    severity: 'caution',
    backgroundColor: '#fff9e3',
    textColor: '#000'
  },
  
  '#pscar': {
    // Severe delayed reaction - beta-lactams not recommended
    betaLactam: "History of severe delayed penicillin allergy noted. Beta-lactam based therapy is not generally recommended. However, in some circumstances the benefit may outweigh the risk - discuss antibiotic choice with ID/Micro.",
    nonBetaLactam: "History of severe delayed penicillin allergy noted. Beta-lactam based therapy is not generally recommended. However, in some circumstances the benefit may outweigh the risk - discuss antibiotic choice with ID/Micro.",
    severity: 'warning',
    backgroundColor: '#ffebee',
    textColor: '#b71c1c'
  },
  
  '#scar': {
    // Same as pscar
    betaLactam: "History of severe delayed penicillin allergy noted. Beta-lactam based therapy is not generally recommended. However, in some circumstances the benefit may outweigh the risk - discuss antibiotic choice with ID/Micro.",
    nonBetaLactam: "History of severe delayed penicillin allergy noted. Beta-lactam based therapy is not generally recommended. However, in some circumstances the benefit may outweigh the risk - discuss antibiotic choice with ID/Micro.",
    severity: 'warning',
    backgroundColor: '#ffebee',
    textColor: '#b71c1c'
  }
};

/**
 * Check if a drug name matches a specific drug class pattern
 * @param {string} drugName - The drug name to check
 * @param {string[]} patterns - Array of patterns to match against
 * @returns {boolean} - True if drug matches any pattern
 */
export const matchesDrugClass = (drugName, patterns) => {
  if (!drugName || !Array.isArray(patterns)) return false;
  
  const lowerDrugName = drugName.toLowerCase();
  return patterns.some(pattern => lowerDrugName.includes(pattern.toLowerCase()));
};

/**
 * Classify a drug name into its drug class
 * @param {string} drugName - The drug name to classify
 * @returns {Object} - Classification results
 */
export const classifyDrug = (drugName) => {
  if (!drugName) return { isPenicillin: false, isCephalosporin: false, isCarbapenem: false, isBetaLactam: false };
  
  const isPenicillin = matchesDrugClass(drugName, DRUG_PATTERNS.PENICILLINS);
  const isCephalosporin = matchesDrugClass(drugName, DRUG_PATTERNS.CEPHALOSPORINS);
  const isCarbapenem = matchesDrugClass(drugName, DRUG_PATTERNS.CARBAPENEMS);
  const isBetaLactam = isPenicillin || isCephalosporin || isCarbapenem;
  
  return {
    isPenicillin,
    isCephalosporin,
    isCarbapenem,
    isBetaLactam,
    drugName: drugName.toLowerCase()
  };
};

/**
 * Extract drug names from treatment recommendations
 * Handles Sanity recommendation structure with treatment1/treatment2 arrays
 * @param {Object} recommendation - Recommendation object from Sanity
 * @returns {string[]} - Array of unique drug names found
 */
export const extractDrugNames = (recommendation) => {
  if (!recommendation || typeof recommendation !== 'object') {
    return [];
  }

  const drugNames = [];
  
  // Extract from treatment1 and treatment2 arrays
  const allTreatments = [
    ...(recommendation.treatment1 || []),
    ...(recommendation.treatment2 || [])
  ];
  
  allTreatments.forEach(treatmentItem => {
    // Check if titleDisplay exists on the treatment object
    if (treatmentItem?.treatment?.titleDisplay && typeof treatmentItem.treatment.titleDisplay === 'string') {
      drugNames.push(treatmentItem.treatment.titleDisplay.trim());
    }
    
    // Also check antimicrobialUnit.title as fallback
    if (treatmentItem?.treatment?.antimicrobialUnit?.title && typeof treatmentItem.treatment.antimicrobialUnit.title === 'string') {
      drugNames.push(treatmentItem.treatment.antimicrobialUnit.title.trim());
    }
  });

  // Remove duplicates and empty strings
  return [...new Set(drugNames.filter(name => name.length > 0))];
};

/**
 * Analyze treatment context to determine drug classes present
 * @param {Object} recommendation - Single recommendation object with treatment1/treatment2
 * @returns {Object} - Analysis of drug classes present
 */
export const analyzeTreatmentContext = (recommendation) => {
  const drugNames = extractDrugNames(recommendation);
  
  // Classify each drug
  const classifications = drugNames.map(classifyDrug);
  
  const context = {
    drugNames,
    classifications,
    hasPenicillins: classifications.some(c => c.isPenicillin),
    hasCephalosporins: classifications.some(c => c.isCephalosporin),
    hasCarbapenems: classifications.some(c => c.isCarbapenem),
    hasBetaLactams: classifications.some(c => c.isBetaLactam),
    hasNonBetaLactams: classifications.some(c => !c.isBetaLactam)
  };

  // Determine primary beta-lactam type for messaging
  if (context.hasCarbapenems) {
    context.primaryBetaLactamType = 'carbapenem';
  } else if (context.hasCephalosporins) {
    context.primaryBetaLactamType = 'cephalosporin';
  } else if (context.hasPenicillins) {
    context.primaryBetaLactamType = 'penicillin';
  } else {
    context.primaryBetaLactamType = null;
  }

  return context;
};

/**
 * Extract allergy code from events array
 * @param {string[]} events - Array of events from recommendation pathway
 * @returns {string|null} - Allergy code (e.g. '#spot') or null
 */
export const extractAllergyFromEvents = (events = []) => {
  if (!Array.isArray(events)) {
    return null;
  }

  // Find first event that starts with '#' (allergy code)
  for (const event of events) {
    if (typeof event === 'string' && event.startsWith('#')) {
      return event;
    }
  }
  
  return null;
};

/**
 * Get contextual allergy message based on allergy type and treatments
 * @param {string} allergyCode - Allergy code (e.g. '#immed')
 * @param {Object} treatmentContext - Result from analyzeTreatmentContext()
 * @returns {Object|null} - Contextual message object or null
 */
export const getContextualAllergyMessage = (allergyCode, treatmentContext) => {
  if (!allergyCode || !ALLERGY_MESSAGES[allergyCode]) {
    return null;
  }

  const messageConfig = ALLERGY_MESSAGES[allergyCode];
  let selectedMessage;

  // Determine which message variant to use based on context
  switch (allergyCode) {
    case '#spot':
      // Simple message for children - always show if there are betalactams
      if (treatmentContext.hasBetaLactams) {
        selectedMessage = messageConfig.default;
      } else {
        return null; // Don't show if no betalactam recommended
      }
      break;

    case '#immed':
    case '#anaphylaxis':
      // Context-aware messaging for immediate allergies
      if (!treatmentContext.hasBetaLactams) {
        return null; // No beta-lactams recommended, no message needed
      }
      
      if (treatmentContext.primaryBetaLactamType === 'carbapenem') {
        selectedMessage = messageConfig.carbapenem;
      } else if (treatmentContext.primaryBetaLactamType === 'cephalosporin') {
        selectedMessage = messageConfig.cephalosporin;
      } else {
        selectedMessage = messageConfig.general;
      }
      break;

    case '#pscar':
    case '#scar':
      // Severe delayed reactions
      if (treatmentContext.hasBetaLactams) {
        selectedMessage = messageConfig.betaLactam;
      } else if (treatmentContext.hasNonBetaLactams) {
        selectedMessage = messageConfig.nonBetaLactam;
      } else {
        return null; // No relevant treatments
      }
      break;

    default:
      return null;
  }

  if (!selectedMessage) {
    return null;
  }

  return {
    message: selectedMessage,
    severity: messageConfig.severity,
    backgroundColor: messageConfig.backgroundColor,
    textColor: messageConfig.textColor,
    allergyCode,
    context: treatmentContext
  };
};

/**
 * Get contextual allergy messages from events and recommendation
 * @param {string[]} events - Events array
 * @param {Object} recommendation - Single recommendation object with treatment1/treatment2
 * @returns {Object[]} - Array of contextual message objects
 */
export const getContextualAllergyMessages = (events = [], recommendation = null) => {
  const allergyCode = extractAllergyFromEvents(events);
  
  if (!allergyCode) {
    return [];
  }

  const treatmentContext = analyzeTreatmentContext(recommendation);
  const message = getContextualAllergyMessage(allergyCode, treatmentContext);
  
  return message ? [message] : [];
};

/**
 * React hook for contextual allergy messages
 * @param {string[]} events - Events array
 * @param {Object} recommendation - Single recommendation object with treatment1/treatment2
 * @returns {Object} - Allergy message data with context
 */
export const useContextualAllergyMessages = (events = [], recommendation = null) => {
  const messages = getContextualAllergyMessages(events, recommendation);
  const treatmentContext = analyzeTreatmentContext(recommendation);
  
  const hasMessages = messages.length > 0;
  const hasWarnings = messages.some(m => m && m.severity === 'warning');
  const hasCautions = messages.some(m => m && m.severity === 'caution');
  
  return {
    messages,
    hasMessages,
    hasWarnings,
    hasCautions,
    allergyCode: extractAllergyFromEvents(events),
    treatmentContext
  };
};

// Legacy support - keep the old functions but mark as deprecated
export const getAllergyMessage = (allergyCode) => {
  console.warn('getAllergyMessage is deprecated. Use getContextualAllergyMessage instead.');
  const config = ALLERGY_MESSAGES[allergyCode];
  return config ? {
    message: config.default || config.general || Object.values(config)[0],
    severity: config.severity,
    backgroundColor: config.backgroundColor,
    textColor: config.textColor,
    allergyCode
  } : null;
};

export const getActiveAllergyMessages = (events = []) => {
  console.warn('getActiveAllergyMessages is deprecated. Use getContextualAllergyMessages instead.');
  const allergyCode = extractAllergyFromEvents(events);
  const message = getAllergyMessage(allergyCode);
  return message ? [message] : [];
};

export const useAllergyMessages = (events = []) => {
  console.warn('useAllergyMessages is deprecated. Use useContextualAllergyMessages instead.');
  return {
    messages: getActiveAllergyMessages(events),
    hasMessages: getActiveAllergyMessages(events).length > 0,
    hasWarnings: getActiveAllergyMessages(events).some(m => m?.severity === 'warning'),
    hasCautions: getActiveAllergyMessages(events).some(m => m?.severity === 'caution'),
    allergyCode: extractAllergyFromEvents(events)
  };
};